import * as React from "react";
import { FetchError } from "@/shared/util/fetch.ts";
import { invariant } from "@/shared/util/invariant.ts";

type SaveResponse = Pick<FetchError, "status" | "statusText">;

interface ContextType {
  saveFailedHash: ReadonlyMap<string, SaveResponse>;
  saveFailed: (key: string, error_object: unknown) => void;
  saveSucceeded: (key: string) => void;
}

const Context = React.createContext<ContextType | null>(null);

export const SaveFailedProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [saveFailedHash, setSaveFailedHash] = React.useState<
    ContextType["saveFailedHash"]
  >(() => new Map());

  const value = React.useMemo<ContextType>(() => {
    function saveFailed(key: string, error_object: unknown) {
      // in case people call us due to thrown errors other than HTTP ones
      if (!(error_object instanceof FetchError)) {
        return;
      }

      setSaveFailedHash((saveFailedHash) => {
        const newMap = new Map(saveFailedHash);

        // only store the props, not the entire error
        const { status, statusText } = error_object;

        newMap.set(key, { status, statusText });

        return newMap;
      });
    }

    function saveSucceeded(key: string) {
      setSaveFailedHash((saveFailedHash) => {
        if (!saveFailedHash.has(key)) {
          return saveFailedHash;
        }

        const newMap = new Map(saveFailedHash);

        newMap.delete(key);

        return newMap;
      });
    }

    return { saveFailedHash, saveFailed, saveSucceeded };
  }, [saveFailedHash]);

  return <Context value={value}>{children}</Context>;
};

function useSaveFailedContext() {
  const context = React.use(Context);

  invariant(
    context,
    "useSaveFailedContext must be used within a SaveFailedProvider",
  );

  return context;
}

export function useSaveFailed() {
  const { saveFailed } = useSaveFailedContext();

  return saveFailed;
}

export function useSaveSucceeded() {
  const { saveSucceeded } = useSaveFailedContext();

  return saveSucceeded;
}

export function useFailedResponse(key: string) {
  const { saveFailedHash } = useSaveFailedContext();

  return saveFailedHash.get(key);
}
